// The moderation console (the "dashboard" for Eddy and moderators). Operate mode: dense where a tool
// should be dense, quiet everywhere else. A queue ordered oldest first against the promise of 2 working days,
// one row per reported thing (its reports grouped under it), the thing in context beside it, one-key
// actions (J/K to move, H hide, R remove, W warn, S suspend, D dismiss, / to filter, ? for the keys),
// every action needing a reason and landing in the audit log.
// Production: admin.biblestandard.app behind Cloudflare Access, calling /v1/admin/* (plan §8.1).
import { reportDue } from "../lib/reportDue";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { createContext, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Avatar } from "../components/Avatar";
import { Badge, Chip } from "../components/Badge";
import { Button } from "../components/Button";
import { Dialog } from "../components/Dialog";
import { Field, Input, Textarea } from "../components/Field";
import { Icon, type IconName } from "../components/Icon";
import { Logo } from "../components/Logo";
import { Post } from "../components/Post";
import { PostImages } from "../components/PostImages";
import { Skeleton } from "../components/Skeleton";
import ui from "../components/ui.module.css";
import { API_URL, apiBase } from "../api/client";
import { createMockAdmin, type AdminSource, type ModAction, type Report, type ReportStatus, type Switch } from "../data/admin";
import { adminToken, createApiAdmin } from "../data/adminApi";
import { ApiError } from "../data/problem";
import type { PostImage } from "../data/types";
import { longTime, shortTime } from "../lib/time";
import { useIndicator } from "../lib/useIndicator";
import { useTitle } from "../lib/useTitle";
import s from "./admin.module.css";

type Section = "reports" | "switches" | "terms" | "audit";
type Kind = Report["target"]["kind"];

export const AdminContext = createContext<AdminSource | null>(null);
const fallback = createMockAdmin();
const useAdmin = () => useContext(AdminContext) ?? fallback;

export const Route = createFileRoute("/admin")({
  validateSearch: (q: Record<string, unknown>): { section?: Section; status?: ReportStatus } => ({
    ...(q.section === "switches" || q.section === "audit" || q.section === "reports" || q.section === "terms" ? { section: q.section } : {}),
    ...(q.status === "actioned" || q.status === "dismissed" || q.status === "open" ? { status: q.status } : {}),
  }),
  component: Console,
});


const SECTIONS: { key: Section; label: string; icon: IconName }[] = [
  { key: "reports", label: "Reports", icon: "flag" },
  { key: "switches", label: "Switches", icon: "pause" },
  { key: "terms", label: "Word filter", icon: "block" },
  { key: "audit", label: "Audit log", icon: "contents" },
];

const KIND_LABEL: Record<Kind, string> = { post: "Post", comment: "Comment", journey_note: "Plan note", user: "Profile" };
const KIND_PLURAL: Record<Kind, string> = { post: "Posts", comment: "Comments", journey_note: "Plan notes", user: "Profiles" };

const ACTION_LABEL: Record<ModAction, string> = { hide: "Hidden", remove: "Removed", warn: "Author warned", suspend: "Suspended", dismiss: "Dismissed", restore: "Restored" };

/** Typing in a field (or a dialog is open): the console's single keys stand down. */
const typing = (t: EventTarget | null) => !!(t as HTMLElement | null)?.closest?.("input, textarea, select, [contenteditable], dialog");

function Console() {
  useTitle("Moderation");
  const { section = "reports" } = Route.useSearch();
  // Against the API: the admin token, typed here and kept in sessionStorage only.
  const [token, setToken] = useState(() => (API_URL ? adminToken.get() : null));
  const admin = useMemo(() => (API_URL && token ? createApiAdmin({ baseUrl: apiBase(), token }) : null), [token]);
  if (API_URL && !admin) return <TokenGate onToken={(t) => (adminToken.set(t), setToken(t))} />;
  return (
    <AdminContext.Provider value={admin}>
      {admin && <TokenCheck onBad={() => (adminToken.set(null), setToken(null))} />}
      <div className={s.console}>
        <a className="skip-link" href="#main">
          Skip to content
        </a>
        <aside className={s.side}>
          <div className={s.brand}>
            <Link to="/home" className={s.brandMark} aria-label="Fellowship, back to the app">
              <Logo size={1.5} wordSize={1.25} />
            </Link>
            <Badge tone="accent" icon="shield">
              Moderation
            </Badge>
          </div>
          <nav aria-label="Console" className={s.navWrap}>
            <ul className={s.nav}>
              {SECTIONS.map(({ key, label, icon }) => (
                <li key={key}>
                  <Link to="/admin" search={{ section: key }} className={s.navItem} aria-current={section === key ? "page" : undefined}>
                    <Icon name={icon} size={18} />
                    <span className={s.navLabel}>{label}</span>
                    {key === "reports" && <OpenCount />}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
          <div className={s.sideFoot}>
            <p className={s.note}>
              <span className={s.dot} data-live={API_URL ? "api" : "preview"} aria-hidden="true" />
              {API_URL ? "Connected with the admin token. Every action is written to the audit log." : "Preview data. In production this lives at admin.biblestandard.app behind Cloudflare Access."}
            </p>
            <Link to="/home" className={s.exit}>
              <Icon name="back" size={16} />
              Back to Fellowship
            </Link>
          </div>
        </aside>
        <main id="main" className={s.main} tabIndex={-1}>
          {section === "reports" && <Reports />}
          {section === "switches" && <Switches />}
          {section === "terms" && <Terms />}
          {section === "audit" && <Audit />}
        </main>
      </div>
    </AdminContext.Provider>
  );
}

function TokenGate({ onToken }: { onToken: (t: string) => void }) {
  const [value, setValue] = useState("");
  return (
    <main id="main" className={s.gate}>
      <form
        className={s.gateCard}
        onSubmit={(e) => {
          e.preventDefault();
          if (value.trim()) onToken(value.trim());
        }}
      >
        <Logo size={1.75} wordSize={1.375} />
        <div className={s.gateHead}>
          <h1 className={s.gateTitle}>Moderation</h1>
          <p className={s.muted}>Paste the admin token to open the console.</p>
        </div>
        <Field label="Admin token" id="admin-token" hint="Kept in this tab only, and forgotten when it closes.">
          {(p) => <Input {...p} type="password" autoComplete="off" spellCheck={false} value={value} onChange={(e) => setValue(e.target.value)} />}
        </Field>
        <Button type="submit" variant="primary" wide disabled={!value.trim()}>
          Open the console
        </Button>
      </form>
    </main>
  );
}

/** A wrong or expired token: back to the gate. */
function TokenCheck({ onBad }: { onBad: () => void }) {
  const admin = useAdmin();
  const { error } = useQuery({ queryKey: ["admin", "terms"], queryFn: () => admin.terms(), retry: false });
  useEffect(() => {
    if (error instanceof ApiError && (error.status === 401 || error.status === 403)) onBad();
  }, [error, onBad]);
  return null;
}

function OpenCount() {
  const admin = useAdmin();
  const { data } = useQuery({ queryKey: ["admin", "reports", "open"], queryFn: () => admin.reports("open") });
  if (!data?.length) return null;
  return (
    <span className={s.count}>
      {data.length}
      <span className="visually-hidden"> open</span>
    </span>
  );
}

/** A section's heading row: the title, one line under it, and its controls on the right. */
function Head({ title, line, children }: { title: string; line?: string; children?: ReactNode }) {
  return (
    <header className={s.head}>
      <div className={s.headWords}>
        <h1 className={s.title}>{title}</h1>
        {line && <p className={s.muted}>{line}</p>}
      </div>
      {children && <div className={s.headTools}>{children}</div>}
    </header>
  );
}

/** Time left on the 2-working-day promise (src/lib/reportDue.ts). */
function dueIn(r: Report, now: number) {
  const left = reportDue(new Date(r.createdAt).getTime()) - now;
  if (left <= 0) return { text: "Overdue", urgent: true };
  const h = Math.floor(left / 3_600_000);
  return { text: h < 1 ? "Due within the hour" : `Due in ${h} h`, urgent: h < 6 };
}

function targetLabel(r: Report) {
  return r.target.kind === "post" ? (r.post?.title ?? r.post?.ref?.label ?? "Post") : r.target.kind === "comment" ? "Comment" : r.target.kind === "journey_note" ? "Plan note" : "Profile";
}

/** The reports on one thing, grouped by reason: "Spam ×2", newest time last. */
function groupReasons(r: Report) {
  const map = new Map<string, { reason: string; count: number; last: string }>();
  for (const x of r.reasons) {
    const g = map.get(x.reason);
    if (g) {
      g.count++;
      if (x.at > g.last) g.last = x.at;
    } else map.set(x.reason, { reason: x.reason, count: 1, last: x.at });
  }
  return [...map.values()].sort((a, b) => b.count - a.count);
}

const ACTIONS: { a: ModAction; label: string; key: string; icon: IconName }[] = [
  { a: "hide", label: "Hide", key: "H", icon: "lock" },
  { a: "remove", label: "Remove", key: "R", icon: "trash" },
  { a: "warn", label: "Warn author", key: "W", icon: "flag" },
  { a: "suspend", label: "Suspend", key: "S", icon: "pause" },
  { a: "dismiss", label: "Dismiss", key: "D", icon: "check" },
];

function Reports() {
  const admin = useAdmin();
  const qc = useQueryClient();
  const { status = "open" } = Route.useSearch();
  const { data: reports, isPending } = useQuery({ queryKey: ["admin", "reports", status], queryFn: () => admin.reports(status) });
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [pending, setPending] = useState<ModAction | null>(null);
  const [reason, setReason] = useState("");
  const [days, setDays] = useState(7);
  const [kind, setKind] = useState<Kind | "all">("all");
  const [filter, setFilter] = useState("");
  const [keys, setKeys] = useState(false);
  const reasonRef = useRef<HTMLTextAreaElement>(null);
  const filterRef = useRef<HTMLInputElement>(null);
  const tabsRef = useIndicator<HTMLElement>();
  const [now] = useState(() => Date.now());

  const kinds = useMemo(() => {
    const counts = new Map<Kind, number>();
    for (const r of reports ?? []) counts.set(r.target.kind, (counts.get(r.target.kind) ?? 0) + 1);
    return counts;
  }, [reports]);

  const shown = useMemo(() => {
    const term = filter.trim().toLowerCase().replace(/^@/, "");
    return (reports ?? []).filter(
      (r) =>
        (kind === "all" || r.target.kind === kind) &&
        (!term || r.author.handle.toLowerCase().includes(term) || r.author.displayName.toLowerCase().includes(term) || r.reasons.some((x) => x.reason.toLowerCase().includes(term)) || targetLabel(r).toLowerCase().includes(term)),
    );
  }, [reports, kind, filter]);

  const selected = useMemo(() => shown.find((r) => r.id === selectedId) ?? shown[0] ?? null, [shown, selectedId]);

  const act = useMutation({
    mutationFn: ({ id, action, why, days }: { id: string; action: ModAction; why: string; days?: number }) => admin.act(id, action, why, days),
    onSuccess: () => {
      setPending(null);
      setReason("");
      void qc.invalidateQueries({ queryKey: ["admin"] });
    },
  });

  const choose = (a: ModAction) => {
    setPending(a);
    setTimeout(() => reasonRef.current?.focus(), 0);
  };

  // Keyboard triage. Ignored while typing.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (typing(e.target) || e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === "?") {
        e.preventDefault();
        setKeys(true);
        return;
      }
      if (e.key === "/") {
        e.preventDefault();
        filterRef.current?.focus();
        return;
      }
      if (!shown.length) return;
      const i = shown.findIndex((r) => r.id === selected?.id);
      const open = status === "open";
      const map: Record<string, () => void> = {
        j: () => setSelectedId(shown[Math.min(i + 1, shown.length - 1)]!.id),
        k: () => setSelectedId(shown[Math.max(i - 1, 0)]!.id),
        h: () => open && choose("hide"),
        r: () => open && choose("remove"),
        w: () => open && choose("warn"),
        s: () => open && choose("suspend"),
        d: () => open && choose("dismiss"),
      };
      const fn = map[e.key.toLowerCase()];
      if (fn) {
        e.preventDefault();
        fn();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [shown, selected, status]);

  // Keep the selected row in view as J and K move through a long queue.
  useEffect(() => {
    if (!selected) return;
    document.getElementById(`q-${selected.id}`)?.scrollIntoView({ block: "nearest" });
  }, [selected]);

  return (
    <div className={s.reports}>
      <Head title="Reports" line={status === "open" ? "Oldest first. Each is answered within 24 hours of the first report." : undefined}>
        <nav className={`${ui.tabs} ${s.statusTabs}`} ref={tabsRef} aria-label="Report status">
          {(["open", "actioned", "dismissed"] as const).map((st) => (
            <Link key={st} to="/admin" search={{ section: "reports", status: st }} className={ui.tab} aria-current={status === st ? "page" : undefined} onClick={() => setSelectedId(null)}>
              {st === "open" ? "Open" : st === "actioned" ? "Actioned" : "Dismissed"}
            </Link>
          ))}
        </nav>
      </Head>

      <div className={s.toolbar}>
        <div className={s.chips} role="group" aria-label="Show">
          <Chip selected={kind === "all"} onClick={() => setKind("all")}>
            All <span className={s.chipCount}>{reports?.length ?? 0}</span>
          </Chip>
          {(["post", "comment", "journey_note", "user"] as const).map((k) => (
            <Chip key={k} selected={kind === k} onClick={() => setKind(k)} disabled={!kinds.get(k) && kind !== k}>
              {KIND_PLURAL[k]} <span className={s.chipCount}>{kinds.get(k) ?? 0}</span>
            </Chip>
          ))}
        </div>
        <label className={s.search}>
          <Icon name="search" size={16} />
          <span className="visually-hidden">Filter reports</span>
          <input ref={filterRef} type="search" placeholder="Filter by handle or reason" value={filter} onChange={(e) => setFilter(e.target.value)} onKeyDown={(e) => e.key === "Escape" && (setFilter(""), e.currentTarget.blur())} />
          <kbd className={s.kbd}>/</kbd>
        </label>
      </div>

      {isPending ? (
        <div className={s.split}>
          <div className={s.queueSkeleton}>
            {[0, 1, 2].map((n) => (
              <Skeleton key={n} height={76} shape="block" />
            ))}
          </div>
        </div>
      ) : shown.length === 0 ? (
        <div className={s.empty}>
          <Icon name="check" size={22} />
          <p>{reports?.length ? "Nothing matches that filter." : status === "open" ? "Nothing waiting. Every report has been answered." : "None yet."}</p>
        </div>
      ) : (
        <div className={s.split}>
          <ol className={s.queue} aria-label="Queue, oldest first">
            {shown.map((r) => {
              const due = dueIn(r, now);
              const on = selected?.id === r.id;
              return (
                <li key={r.id} id={`q-${r.id}`}>
                  <button type="button" className={s.item} aria-current={on ? "true" : undefined} onClick={() => setSelectedId(r.id)}>
                    <span className={s.itemTop}>
                      <span className={s.kind}>{KIND_LABEL[r.target.kind]}</span>
                      {status === "open" ? (
                        <span className={due.urgent ? `${s.due} ${s.urgent}` : s.due}>{due.text}</span>
                      ) : (
                        r.resolution && <span className={s.due}>{ACTION_LABEL[r.resolution.action]}</span>
                      )}
                    </span>
                    <span className={s.itemTitle}>{targetLabel(r)}</span>
                    <span className={s.itemMeta}>
                      {r.reasons[0]?.reason} · {r.reasons.length} {r.reasons.length === 1 ? "report" : "reports"} · @{r.author.handle}
                    </span>
                  </button>
                </li>
              );
            })}
          </ol>

          {selected && (
            <section className={s.detail} aria-label="Report">
              <div className={s.context}>
                <div className={s.contextHead}>
                  <p className={s.label}>Reported {KIND_LABEL[selected.target.kind].toLowerCase()}</p>
                  <code className={s.id}>{selected.target.id}</code>
                </div>
                {selected.post && <Post post={selected.post} variant="preview" />}
                {selected.post && selected.post.images.length > 0 && <ReportedPhotos images={selected.post.images} />}
                {selected.comment && (
                  <blockquote className={s.comment}>
                    <p>{selected.comment.body}</p>
                    <footer>{selected.comment.author.displayName}</footer>
                  </blockquote>
                )}
                {selected.user && (
                  <p className={s.person}>
                    <Avatar user={selected.user} size={40} /> {selected.user.displayName} <span className={s.muted}>@{selected.user.handle}</span>
                  </p>
                )}
              </div>

              <dl className={s.facts}>
                <div>
                  <dt>Author</dt>
                  <dd>
                    {selected.author.displayName} <span className={s.muted}>@{selected.author.handle}</span>
                  </dd>
                </div>
                <div>
                  <dt>Earlier actions</dt>
                  <dd>{selected.priorActions === 0 ? "None" : <Badge tone="accent">{`${selected.priorActions} on this account`}</Badge>}</dd>
                </div>
                <div>
                  <dt>First reported</dt>
                  <dd>
                    <time dateTime={selected.createdAt} title={longTime(selected.createdAt)}>
                      {longTime(selected.createdAt)}
                    </time>
                  </dd>
                </div>
                <div>
                  <dt>Reports</dt>
                  <dd>
                    <ul className={s.reasons}>
                      {groupReasons(selected).map((g) => (
                        <li key={g.reason}>
                          <span>{g.reason}</span>
                          {g.count > 1 && <span className={s.times}>×{g.count}</span>}
                          <time className={s.muted} dateTime={g.last} title={longTime(g.last)}>
                            {shortTime(g.last)}
                          </time>
                        </li>
                      ))}
                    </ul>
                  </dd>
                </div>
                {selected.resolution && (
                  <div>
                    <dt>Decision</dt>
                    <dd className={s.decision}>
                      <Badge tone={selected.resolution.action === "dismiss" ? "neutral" : "solid"}>{ACTION_LABEL[selected.resolution.action]}</Badge>
                      <span>{selected.resolution.reason}</span>
                      <time className={s.muted} dateTime={selected.resolution.at} title={longTime(selected.resolution.at)}>
                        {shortTime(selected.resolution.at)}
                      </time>
                    </dd>
                  </div>
                )}
              </dl>

              {selected.status === "open" && (
                <div className={s.decide}>
                  <div className={s.actions} role="group" aria-label="Decide">
                    {ACTIONS.map(({ a, label, key, icon }) => (
                      <button key={a} type="button" className={pending === a ? `${s.act} ${s.actOn}` : s.act} aria-pressed={pending === a} aria-keyshortcuts={key} onClick={() => choose(a)}>
                        <Icon name={icon} size={16} />
                        {label}
                        <kbd className={s.kbd}>{key}</kbd>
                      </button>
                    ))}
                  </div>
                  {pending && (
                    <form
                      className={s.reasonForm}
                      onSubmit={(e) => {
                        e.preventDefault();
                        if (reason.trim()) act.mutate({ id: selected.id, action: pending, why: reason.trim(), ...(pending === "suspend" ? { days } : {}) });
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Escape") {
                          e.preventDefault();
                          setPending(null);
                        }
                      }}
                    >
                      <Field id="mod-reason" label={`Reason for “${ACTIONS.find((x) => x.a === pending)!.label.toLowerCase()}”`} hint="The author and the reporter see a plain version of this.">
                        {(p) => <Textarea {...p} ref={reasonRef} rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />}
                      </Field>
                      {pending === "suspend" && (
                        <label className={s.days}>
                          For
                          <select className={ui.select} value={days} onChange={(e) => setDays(Number(e.target.value))}>
                            {[1, 7, 30, 90].map((d) => (
                              <option key={d} value={d}>
                                {d} {d === 1 ? "day" : "days"}
                              </option>
                            ))}
                          </select>
                        </label>
                      )}
                      {act.isError && (
                        <p className={ui.error} role="alert">
                          {(act.error as Error).message}
                        </p>
                      )}
                      <div className={s.formEnd}>
                        <Button size="sm" variant="ghost" onClick={() => setPending(null)}>
                          Cancel
                        </Button>
                        <Button type="submit" size="sm" variant="primary" disabled={!reason.trim() || act.isPending} busy={act.isPending}>
                          Confirm
                        </Button>
                      </div>
                    </form>
                  )}
                </div>
              )}
            </section>
          )}
        </div>
      )}
      <p className={s.keys}>
        <button type="button" className={s.keysButton} onClick={() => setKeys(true)}>
          <kbd className={s.kbd}>?</kbd> Keyboard shortcuts
        </button>
      </p>
      <Dialog open={keys} onClose={() => setKeys(false)} size="sm" title="Console shortcuts" dismissible>
        <dl className={s.keyList}>
          {(
            [
              ["J  K", "Next and previous report"],
              ["H", "Hide"],
              ["R", "Remove"],
              ["W", "Warn the author"],
              ["S", "Suspend"],
              ["D", "Dismiss"],
              ["/", "Filter the queue"],
              ["Esc", "Cancel a decision"],
            ] as const
          ).map(([k, what]) => (
            <div key={k}>
              <dt>
                {k.split("  ").map((x) => (
                  <kbd key={x} className={s.kbd}>
                    {x}
                  </kbd>
                ))}
              </dt>
              <dd>{what}</dd>
            </div>
          ))}
        </dl>
      </Dialog>
    </div>
  );
}

function Switches() {
  const admin = useAdmin();
  const qc = useQueryClient();
  const { data, isPending } = useQuery({ queryKey: ["admin", "switches"], queryFn: () => admin.switches() });
  return (
    <div>
      <Head title="Switches" line="Close a feature or partner without an app update. A closed feature’s routes answer with your message." />
      {isPending ? (
        <Skeleton height={200} shape="block" />
      ) : (
        <ul className={s.switches}>
          {data?.map((sw) => (
            <SwitchRow
              key={sw.key}
              sw={sw}
              onSave={async (state, message) => {
                await admin.setSwitch(sw.key, state, message);
                void qc.invalidateQueries({ queryKey: ["admin"] });
              }}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

const STATE_LABEL: Record<Switch["state"], string> = { on: "On", maintenance: "Maintenance", off: "Off" };

function SwitchRow({ sw, onSave }: { sw: Switch; onSave: (state: Switch["state"], message: string | null) => Promise<void> }) {
  const [state, setState] = useState(sw.state);
  const [message, setMessage] = useState(sw.message ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dirty = state !== sw.state || (message || null) !== sw.message;
  return (
    <li className={s.switchRow} data-state={sw.state}>
      <div className={s.switchWords}>
        <p className={s.switchName}>
          <span className={s.stateDot} data-state={sw.state} aria-hidden="true" />
          {sw.label}
        </p>
        <p className={s.switchMeta}>
          <code>{sw.key}</code>
          {sw.updatedAt ? (
            <>
              {" "}
              · {STATE_LABEL[sw.state]} since{" "}
              <time dateTime={sw.updatedAt} title={longTime(sw.updatedAt)}>
                {longTime(sw.updatedAt)}
              </time>
            </>
          ) : (
            <> · {STATE_LABEL[sw.state]}, never changed</>
          )}
        </p>
      </div>
      <fieldset className={s.states}>
        <legend className="visually-hidden">State of {sw.label}</legend>
        {(["on", "maintenance", "off"] as const).map((st) => (
          <label key={st} className={state === st ? `${s.state} ${s.stateOn} ${s[`st_${st}`]}` : s.state}>
            <input type="radio" name={sw.key} checked={state === st} onChange={() => setState(st)} />
            {STATE_LABEL[st]}
          </label>
        ))}
      </fieldset>
      <div className={s.switchEnd}>
        {state !== "on" && (
          <input className={`${ui.field} ${s.message}`} placeholder="What readers see, for example Back by 3pm." maxLength={280} value={message} onChange={(e) => setMessage(e.target.value)} aria-label={`Message for ${sw.label}`} />
        )}
        <Button
          size="sm"
          variant={dirty ? "primary" : "secondary"}
          disabled={!dirty || saving}
          busy={saving}
          onClick={async () => {
            setSaving(true);
            setError(null);
            try {
              await onSave(state, state === "on" ? null : message || null);
            } catch (e) {
              setError((e as Error).message);
            } finally {
              setSaving(false);
            }
          }}
        >
          Apply
        </Button>
      </div>
      {error && (
        <p className={`${ui.error} ${s.switchError}`} role="alert">
          {error}
        </p>
      )}
    </li>
  );
}

function Audit() {
  const admin = useAdmin();
  const [filter, setFilter] = useState("");
  // Newest first, fifty at a time; "Show earlier" pages back by the last entry's time.
  const q = useInfiniteQuery({
    queryKey: ["admin", "audit"],
    queryFn: ({ pageParam }) => admin.audit(pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => (last && last.length >= 50 ? last.at(-1)!.at : undefined),
  });
  const data = q.data ? (q.data.pages[0] === null ? null : q.data.pages.flatMap((p) => p ?? [])) : undefined;
  const term = filter.trim().toLowerCase();
  const rows = data?.filter((a) => !term || [a.actor, a.action, a.target, a.detail ?? ""].some((x) => x.toLowerCase().includes(term)));
  return (
    <div>
      <Head title="Audit log" line="Every admin action and system job that changed something. Kept, never edited.">
        <label className={s.search}>
          <Icon name="search" size={16} />
          <span className="visually-hidden">Filter the log</span>
          <input type="search" placeholder="Filter by action, who or target" value={filter} onChange={(e) => setFilter(e.target.value)} />
        </label>
      </Head>
      {data === null && <p className={s.empty}>This source can’t read the audit log.</p>}
      {data && data.length === 0 && <p className={s.empty}>Nothing has been logged yet.</p>}
      {q.isPending && <Skeleton height={240} shape="block" />}
      {rows && rows.length > 0 && (
        <div className={s.tableWrap}>
          <table className={s.table}>
            <thead>
              <tr>
                <th scope="col">When</th>
                <th scope="col">Who</th>
                <th scope="col">Action</th>
                <th scope="col">Target</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((a) => (
                <tr key={a.id}>
                  <td className={s.nowrap}>
                    <time dateTime={a.at} title={longTime(a.at)}>
                      {shortTime(a.at)}
                    </time>
                  </td>
                  <td>{a.actor}</td>
                  <td>
                    <code className={s.action}>{a.action}</code>
                  </td>
                  <td>
                    <code>{a.target}</code>
                    {a.detail && <span className={s.muted}> {a.detail}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {rows && data && data.length > 0 && rows.length === 0 && <p className={s.empty}>Nothing loaded matches that filter.</p>}
      {data && data.length > 0 && (
        <div className={s.tableFoot}>
          <p className={s.muted}>
            {data.length} {data.length === 1 ? "entry" : "entries"} loaded{q.hasNextPage ? "" : ", the whole log"}
          </p>
          {q.hasNextPage && (
            <Button size="sm" disabled={q.isFetchingNextPage} busy={q.isFetchingNextPage} onClick={() => void q.fetchNextPage()}>
              {q.isFetchingNextPage ? "Loading…" : "Show earlier"}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

function Terms() {
  const admin = useAdmin();
  const qc = useQueryClient();
  const { data, isPending } = useQuery({ queryKey: ["admin", "terms"], queryFn: () => admin.terms() });
  const [term, setTerm] = useState("");
  const change = useMutation({
    mutationFn: ({ t, add }: { t: string; add: boolean }) => admin.setTerm(t, add),
    onSuccess: (_, v) => {
      if (v.add) setTerm("");
      void qc.invalidateQueries({ queryKey: ["admin"] });
    },
  });
  const exists = !!data?.some((t) => t.term.toLowerCase() === term.trim().toLowerCase());
  return (
    <div>
      <Head title="Word filter" line="Words refused in names, posts and comments. Matching ignores case, accents, spacing and letter swaps, and whole words only." />
      <form
        className={s.addTerm}
        onSubmit={(e) => {
          e.preventDefault();
          if (term.trim() && !exists) change.mutate({ t: term.trim(), add: true });
        }}
      >
        <Field label="Add a word" id="term" error={exists ? "That word is already on the list." : change.isError ? (change.error as Error).message : undefined} className={s.addField}>
          {(p) => <Input {...p} maxLength={60} autoComplete="off" spellCheck={false} value={term} onChange={(e) => setTerm(e.target.value)} />}
        </Field>
        <Button type="submit" variant="primary" icon="plus" disabled={!term.trim() || exists || change.isPending} busy={change.isPending && change.variables?.add}>
          Add
        </Button>
      </form>
      {isPending ? (
        <Skeleton height={160} shape="block" />
      ) : data && data.length === 0 ? (
        <p className={s.empty}>The list is empty.</p>
      ) : (
        <div className={s.tableWrap}>
          <table className={s.table}>
            <caption className={s.caption}>
              {data?.length} {data?.length === 1 ? "word" : "words"}
            </caption>
            <thead>
              <tr>
                <th scope="col">Word</th>
                <th scope="col">Added by</th>
                <th scope="col">When</th>
                <th scope="col">
                  <span className="visually-hidden">Remove</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {data?.map((t) => (
                <tr key={t.term}>
                  <td>
                    <code>{t.term}</code>
                  </td>
                  <td>{t.addedBy}</td>
                  <td className={s.nowrap}>
                    <time dateTime={t.createdAt} title={longTime(t.createdAt)}>
                      {shortTime(t.createdAt)}
                    </time>
                  </td>
                  <td className={s.end}>
                    <button type="button" className={s.rowButton} onClick={() => change.mutate({ t: t.term, add: false })}>
                      Remove <span className="visually-hidden">{t.term}</span>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/**
 * A reported post's photos (FW-007): fetched with the admin token from /v1/admin/post-images/{id}
 * (never by an image tag, so the token never leaves this page) and shown from object URLs, which go
 * when the report is closed. A photo already removed shows its words.
 */
function ReportedPhotos({ images }: { images: PostImage[] }) {
  const admin = useAdmin();
  const [urls, setUrls] = useState<Record<string, string | null>>({});
  useEffect(() => {
    let live = true;
    const made: string[] = [];
    for (const im of images)
      admin
        .postImage(im.id)
        .then((url) => {
          made.push(url);
          if (live) setUrls((u) => ({ ...u, [im.id]: url }));
          else URL.revokeObjectURL(url);
        })
        .catch(() => live && setUrls((u) => ({ ...u, [im.id]: null })));
    return () => {
      live = false;
      made.forEach((u) => URL.revokeObjectURL(u));
    };
  }, [admin, images]);
  return (
    <div className={s.photos}>
      <p className={s.label}>{images.length === 1 ? "Its photo" : `Its ${images.length} photos`}</p>
      <PostImages images={images.map((im) => ({ ...im, url: urls[im.id] ?? "" }))} />
      {images
        .filter((im) => urls[im.id] === null)
        .map((im) => (
          <p key={im.id} className={s.muted}>
            Not available any more: {im.alt}
          </p>
        ))}
    </div>
  );
}
